// memorii アプリ本体（lib/9.type/type_tab_content.dart）の16タイプ本番コンテンツを移植。
// 出典: Googleスプレッドシート「性格診断 改正案」> 「16タイプの記載内容」。
// 文言はシートの内容をそのまま反映しており、要約・言い換えは行っていない。
// 変更する場合は必ずシート側を更新してから、このファイルに反映すること。

export type ThreeStrength = {
  title: string;
  description: string;
};

export type SceneCard = {
  title: string;
  description: string;
};

export type EnvironmentItem = {
  title: string;
};

export type PersonalitySection = {
  title: string;
  paragraphs: string[];
};

export type TiringSituation = {
  title: string;
  description: string;
};

export type CompatibilityMatch = {
  type: string;
  subtitle: string;
  note?: string;
  paragraphs: string[];
};

export type TypeTabContent = {
  oneLiner: string;
  strengths: ThreeStrength[];
  strengthsSummary: string;
  scenes: SceneCard[];
  scenesSummary: string;
  environment: EnvironmentItem[];
  personalitySections: PersonalitySection[];
  visitChecklist: string[];
  tiringSituations: TiringSituation[];
  closingMessage: string[];
  naturalFit: CompatibilityMatch[];
  broadening: CompatibilityMatch[];
  needsAwareness: CompatibilityMatch[];
};

export const typeTabContent: Record<string, TypeTabContent> = {
  HSTJ: {
    "oneLiner": "基本を守り、技術を確実に自分のものにする人",
    "strengths": [
      {
        "title": "精度を高める集中力",
        "description": "細かな違いに気づき、仕上がりを少しずつ良くしていけます。"
      },
      {
        "title": "基本を積み上げる継続力",
        "description": "一度覚えたことを反復し、安定した技術へ変えていけます。"
      },
      {
        "title": "手順を守る安定感",
        "description": "決めた流れを丁寧に実行し、再現性のある仕事ができます。"
      }
    ],
    "strengthsSummary": "細かな違いに気づいて完成度を高める集中力があり、基本を繰り返しながら技術を安定させていけます。手順を大切にするため、毎回の仕上がりを大きくぶらさず、着実に信頼を積み上げやすい人です。その積み重ねが、自分らしい技術の強さにつながっていきます。",
    "scenes": [
      {
        "title": "技術練習",
        "description": "カットやカラーなどを繰り返し練習し、精度を上げる場面で力を発揮します。"
      },
      {
        "title": "施術の仕上げ",
        "description": "細部を確認しながら、最後まで丁寧に完成度を整えられます。"
      },
      {
        "title": "ルーティン業務",
        "description": "決められた流れを守りながら、安定して仕事を進められます。"
      }
    ],
    "scenesSummary": "基礎練習を繰り返して技術を磨く場面や、施術の細部を整える仕上げで力を発揮します。決まった流れの中で安定して動くことも得意で、毎日の積み重ねがそのまま実力につながりやすいタイプです。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "教育手順が明確"
      },
      {
        "title": "練習時間を確保できる"
      },
      {
        "title": "技術基準がはっきりしている"
      },
      {
        "title": "落ち着いて集中できる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "教育カリキュラムが段階的か",
      "練習を見てもらえる時間があるか",
      "技術チェックの基準が明確か",
      "毎日の仕事の流れが安定しているか"
    ],
    "tiringSituations": [
      {
        "title": "基準が曖昧",
        "description": "何を目指せばよいか分からない状態が続くと、力を出しにくくなります。"
      },
      {
        "title": "変更が頻繁",
        "description": "手順や方針が何度も変わる環境では、集中力を削られやすいです。"
      },
      {
        "title": "速さだけを求められる",
        "description": "丁寧さより速度だけを優先されると、自分らしい強みを活かしにくくなります。"
      }
    ],
    "closingMessage": [
      "丁寧に積み重ねた練習は、あなたにとって大きな自信になります。急いで結果を出すより、できることを一つずつ増やすほど強みが際立ちます。自分のペースで磨いた技術は、長く使える武器になります。"
    ],
    "naturalFit": [
      {
        "type": "HSEJ",
        "subtitle": "自走する技術探究者",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "自走する技術探究者とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HSTP",
        "subtitle": "しなやかな技術職人",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "しなやかな技術職人とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CSTJ",
        "subtitle": "安心と信頼の伴走者",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "安心と信頼の伴走者は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HRTJ",
        "subtitle": "技術のパイオニア",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "技術のパイオニアは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CREP",
        "subtitle": "直感の冒険家",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "直感の冒険家とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HSTP: {
    "oneLiner": "基本を大切にしながら、目の前に合わせて動ける人",
    "strengths": [
      {
        "title": "基本を身につける力",
        "description": "土台となる技術や手順を丁寧に吸収できます。"
      },
      {
        "title": "現場で合わせる柔軟さ",
        "description": "状況が変わっても、目の前に合わせてやり方を調整できます。"
      },
      {
        "title": "手を動かして覚える力",
        "description": "実践を重ねながら、自分の感覚として技術を身につけられます。"
      }
    ],
    "strengthsSummary": "基本を丁寧に身につける一方で、実際の現場では相手や状況に合わせて柔軟に調整できます。頭で考え続けるより手を動かしながら感覚をつかむのが得意で、経験を重ねるほど対応の幅が広がる人です。その積み重ねが、自分らしい技術の強さにつながっていきます。",
    "scenes": [
      {
        "title": "モデル施術",
        "description": "基本を意識しながら、相手に合わせて細かな調整ができます。"
      },
      {
        "title": "忙しい営業中",
        "description": "予定外のことが起きても、その場で優先順位を変えて対応できます。"
      },
      {
        "title": "技術の反復練習",
        "description": "同じ練習を重ねながら、自分なりのコツをつかめます。"
      }
    ],
    "scenesSummary": "モデル施術のように基本と応用の両方が必要な場面や、営業中の急な変更に強みが出ます。反復練習でも、自分なりのコツを見つけながら吸収できるため、実践の中で徐々に技術を自分のものにしていけます。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "基本を教えてもらえる"
      },
      {
        "title": "現場で工夫できる"
      },
      {
        "title": "実践機会が多い"
      },
      {
        "title": "細かく縛られすぎない"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "基本技術を学べる仕組みがあるか",
      "実践に入る機会が十分にあるか",
      "現場判断を任せてもらえるか",
      "ルールが細かすぎないか"
    ],
    "tiringSituations": [
      {
        "title": "細かく管理される",
        "description": "すべての動きを決められると、自分で調整する良さが出にくくなります。"
      },
      {
        "title": "実践機会が少ない",
        "description": "説明ばかりで手を動かせない環境では、成長を実感しにくいです。"
      },
      {
        "title": "正解が一つしかない",
        "description": "状況に合わせる余地がないと、窮屈さを感じやすくなります。"
      }
    ],
    "closingMessage": [
      "あなたの良さは、基本を崩さずに現場へ合わせられることです。決められた型を覚えたあと、自分なりに微調整できるようになるとさらに強くなります。丁寧さと柔軟さの両方を武器にしてください。"
    ],
    "naturalFit": [
      {
        "type": "HSEP",
        "subtitle": "自分流を磨く職人",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "自分流を磨く職人とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HSTJ",
        "subtitle": "技術を積み上げる職人",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "技術を積み上げる職人とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CSTP",
        "subtitle": "安心感のムードメーカー",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "安心感のムードメーカーは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HRTP",
        "subtitle": "ひらめきのチャレンジャー",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "ひらめきのチャレンジャーは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CREJ",
        "subtitle": "共感のプロデューサー",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "共感のプロデューサーとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HSEJ: {
    "oneLiner": "自分で課題を見つけ、計画して技術を伸ばせる人",
    "strengths": [
      {
        "title": "課題を見つける分析力",
        "description": "自分の技術を振り返り、改善すべき点を見つけられます。"
      },
      {
        "title": "自分で進める自律性",
        "description": "必要なことを調べ、指示を待たずに動けます。"
      },
      {
        "title": "成長を設計する計画力",
        "description": "目標から逆算して、練習や行動を組み立てられます。"
      }
    ],
    "strengthsSummary": "自分の技術を客観的に見て課題を見つけ、必要なことを自分で調べながら改善できます。目標から逆算して練習内容を組み立てることも得意で、明確なゴールがあるほど主体的に技術を伸ばしていける人です。その積み重ねが、自分らしい技術の強さにつながっていきます。",
    "scenes": [
      {
        "title": "自主練習",
        "description": "課題を決めて練習内容を組み立て、効率よく改善できます。"
      },
      {
        "title": "技術チェック後",
        "description": "フィードバックを整理し、次に直すポイントを具体化できます。"
      },
      {
        "title": "新しい技術の習得",
        "description": "情報を集め、段階を踏んで自分の技術に落とし込めます。"
      }
    ],
    "scenesSummary": "自主練習や技術チェック後の振り返りで強みが出やすく、改善点を次の行動へ具体化できます。新しい技術を覚えるときも、情報収集から練習まで自分で計画できるため、目的が明確な環境ほど成長速度が上がります。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "裁量を持って学べる"
      },
      {
        "title": "目標設定がしやすい"
      },
      {
        "title": "技術情報に触れられる"
      },
      {
        "title": "成果基準が明確"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "自主練習の方法を自分で選べるか",
      "目標や評価基準が明確か",
      "新しい技術を学べる機会があるか",
      "自分から提案できる雰囲気か"
    ],
    "tiringSituations": [
      {
        "title": "細かい指示が続く",
        "description": "自分で考える余地が少ないと、意欲が下がりやすくなります。"
      },
      {
        "title": "目的が見えない",
        "description": "何のために取り組むのか不明確だと、集中しづらくなります。"
      },
      {
        "title": "評価基準が曖昧",
        "description": "成長の手応えを確認できない状態が続くと、迷いが増えやすいです。"
      }
    ],
    "closingMessage": [
      "あなたは、誰かに言われる前に改善点を見つけられる人です。目標を自分で決め、そこまでの道筋を作るほど集中力が高まります。考えて終わらず、振り返りを次の練習につなげることで技術はさらに伸びます。"
    ],
    "naturalFit": [
      {
        "type": "HSTJ",
        "subtitle": "技術を積み上げる職人",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "技術を積み上げる職人とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HSEP",
        "subtitle": "自分流を磨く職人",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "自分流を磨く職人とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CSEJ",
        "subtitle": "信頼を築く推進者",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "信頼を築く推進者は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HREJ",
        "subtitle": "未来を形にする技術開拓者",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "未来を形にする技術開拓者は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CRTP",
        "subtitle": "即興パフォーマー",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "即興パフォーマーとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HSEP: {
    "oneLiner": "自分の感覚を大切にしながら、技術を深く磨く人",
    "strengths": [
      {
        "title": "自分の感覚を磨く力",
        "description": "仕上がりへの違和感を拾い、自分なりの基準を育てられます。"
      },
      {
        "title": "深く掘る探究心",
        "description": "気になった技術を自分のペースで深く追いかけられます。"
      },
      {
        "title": "やり方を工夫する柔軟さ",
        "description": "決まった方法にこだわらず、自分に合うやり方を見つけられます。"
      }
    ],
    "strengthsSummary": "仕上がりへの小さな違和感に気づき、自分なりの美意識や基準を育てられます。興味を持った技術はじっくり深く掘り下げ、決められた方法だけに頼らず、自分に合うやり方へ工夫していける人です。その積み重ねが、自分らしい技術の強さにつながっていきます。",
    "scenes": [
      {
        "title": "仕上がりの微調整",
        "description": "質感やシルエットを見ながら、最後の細部まで整えられます。"
      },
      {
        "title": "得意技術の深掘り",
        "description": "興味のある分野を自分で研究し、強みに変えていけます。"
      },
      {
        "title": "個別対応の施術",
        "description": "相手や髪の状態に合わせて、その場でやり方を変えられます。"
      }
    ],
    "scenesSummary": "仕上がりの質感やシルエットを細かく整える場面、得意技術を深掘りする場面で力を発揮します。施術でも一つの方法に固定されず、髪の状態や相手に合わせて自分なりに調整できるため、個性を活かした仕事に向いています。",
    "environment": [
      {
        "title": "自分のやり方を試せる"
      },
      {
        "title": "得意分野を深められる"
      },
      {
        "title": "過度に管理されない"
      },
      {
        "title": "感性を尊重してもらえる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "得意技術を伸ばせる環境か",
      "やり方を工夫する余地があるか",
      "細かく管理されすぎないか",
      "作品や感性を評価してもらえるか"
    ],
    "tiringSituations": [
      {
        "title": "やり方を固定される",
        "description": "細かな手順まで決められると、自分らしい工夫がしにくくなります。"
      },
      {
        "title": "常に急かされる",
        "description": "納得する前に次へ進む状況が続くと、消耗しやすくなります。"
      },
      {
        "title": "個性を出せない",
        "description": "仕上がりや表現に自分の感覚を反映できないと、やりがいを感じにくくなります。"
      }
    ],
    "closingMessage": [
      "あなたは、自分なりに納得できる仕上がりを追いかけられる人です。人と同じやり方に無理に合わせるより、試しながら自分の型を見つけるほど魅力が出ます。自分の美意識を言葉にできるようになると、強みはさらに伝わりやすくなります。"
    ],
    "naturalFit": [
      {
        "type": "HSTP",
        "subtitle": "しなやかな技術職人",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "しなやかな技術職人とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HSEJ",
        "subtitle": "自走する技術探究者",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "自走する技術探究者とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CSEP",
        "subtitle": "自然体のコミュニケーター",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "自然体のコミュニケーターは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HREP",
        "subtitle": "自由発想のクリエイター",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "自由発想のクリエイターは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CRTJ",
        "subtitle": "変化のナビゲーター",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "変化のナビゲーターとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HRTJ: {
    "oneLiner": "新しい技術を、確かな武器へ変えていける人",
    "strengths": [
      {
        "title": "新技術を見つける感度",
        "description": "新しい技法やトレンドに早く気づけます。"
      },
      {
        "title": "試して確かめる実行力",
        "description": "興味を持ったことを実践し、使えるかどうかを判断できます。"
      },
      {
        "title": "習得まで組み立てる計画力",
        "description": "新しい技術を一時的な体験で終わらせず、武器に変えられます。"
      }
    ],
    "strengthsSummary": "新しい技術やトレンドへの感度が高く、気になったものを実際に試して確かめられます。さらに習得までの道筋を組み立てることができるため、流行を追うだけで終わらず、自分の確かな技術として定着させやすい人です。",
    "scenes": [
      {
        "title": "新技術の導入",
        "description": "新しい技法を調べ、試しながら自分の武器へ変えられます。"
      },
      {
        "title": "技術コンテスト",
        "description": "目標を定めて準備し、完成度を高めていけます。"
      },
      {
        "title": "トレンド提案",
        "description": "新しさを取り入れつつ、再現できる形に整理して提案できます。"
      }
    ],
    "scenesSummary": "新技術を導入するときや、コンテストに向けて完成度を高める場面で力を発揮します。トレンドをそのまま真似るのではなく、使える形へ整理して自分の技術に落とし込めるため、新しい提案を継続的に生み出しやすいタイプです。",
    "environment": [
      {
        "title": "新技術を学べる"
      },
      {
        "title": "挑戦を後押ししてくれる"
      },
      {
        "title": "目標を持って取り組める"
      },
      {
        "title": "技術成果を評価してもらえる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "新しい技術を学ぶ機会があるか",
      "コンテストや外部講習に挑戦できるか",
      "目標設定や評価基準が明確か",
      "挑戦した結果を振り返る文化があるか"
    ],
    "tiringSituations": [
      {
        "title": "変化がほとんどない",
        "description": "新しい刺激が少ない環境では、成長の実感を持ちにくくなります。"
      },
      {
        "title": "挑戦を止められる",
        "description": "新しい提案を毎回否定されると、意欲が下がりやすくなります。"
      },
      {
        "title": "計画なく振り回される",
        "description": "変化は好きでも、目的なく方針が変わり続けると疲れやすいです。"
      }
    ],
    "closingMessage": [
      "あなたは新しさに惹かれるだけでなく、それを自分の技術として定着させられる人です。興味を持ったものほど、試す目的と習得までの道筋を決めると強みが活きます。流行を追うだけでなく、自分の得意技術へ変えていってください。"
    ],
    "naturalFit": [
      {
        "type": "HREJ",
        "subtitle": "未来を形にする技術開拓者",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "未来を形にする技術開拓者とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HRTP",
        "subtitle": "ひらめきのチャレンジャー",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "ひらめきのチャレンジャーとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CRTJ",
        "subtitle": "変化のナビゲーター",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "変化のナビゲーターは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HSTJ",
        "subtitle": "技術を積み上げる職人",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "技術を積み上げる職人は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CSEP",
        "subtitle": "自然体のコミュニケーター",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "自然体のコミュニケーターとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HRTP: {
    "oneLiner": "気になった技術を、まず試して吸収できる人",
    "strengths": [
      {
        "title": "まず試す行動力",
        "description": "新しい技術に興味を持ったら、すぐに実践へ移せます。"
      },
      {
        "title": "経験から学ぶ吸収力",
        "description": "やってみた結果から、感覚的にコツをつかめます。"
      },
      {
        "title": "変化に乗る柔軟さ",
        "description": "状況や流行が変わっても、すぐにやり方を切り替えられます。"
      }
    ],
    "strengthsSummary": "新しい技術に興味を持つと、まず試して経験から学べる行動力があります。実践の中でコツをつかむのが得意で、トレンドや状況が変わっても柔軟にやり方を切り替えられるため、変化の多い現場ほど力が出やすい人です。",
    "scenes": [
      {
        "title": "新メニューの体験",
        "description": "新しい施術をまず試し、実践から使い方を覚えられます。"
      },
      {
        "title": "撮影・クリエイティブ",
        "description": "その場のアイデアを形にしながら、表現を広げられます。"
      },
      {
        "title": "忙しい現場対応",
        "description": "予定が変わっても、目の前の状況を見てすぐ動けます。"
      }
    ],
    "scenesSummary": "新メニューを試す場面や撮影など、まず動いてみることが求められる場面で強みが出ます。営業中の予定変更にも対応しやすく、経験の中から学びを拾えるため、変化と実践が多い環境ほど成長しやすいタイプです。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "新しいことを試せる"
      },
      {
        "title": "実践機会が多い"
      },
      {
        "title": "変化のある現場"
      },
      {
        "title": "自由度が高い"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "新しいメニューに挑戦できるか",
      "早い段階から実践機会があるか",
      "現場で工夫する余地があるか",
      "変化を前向きに受け入れる文化か"
    ],
    "tiringSituations": [
      {
        "title": "同じことの繰り返し",
        "description": "変化が少ない日々が続くと、集中力が落ちやすくなります。"
      },
      {
        "title": "準備だけが長い",
        "description": "実践までに時間がかかりすぎると、意欲を保ちにくくなります。"
      },
      {
        "title": "細かな手順に縛られる",
        "description": "やり方を柔軟に変えられない環境では、窮屈さを感じやすいです。"
      }
    ],
    "closingMessage": [
      "あなたの強みは、考えすぎる前に経験へ変えられることです。新しいことに触れたら、まず小さく試すことで学びが加速します。興味が移りやすいときは、身につけたい技術を一つだけ決めて残すと強みが積み上がります。"
    ],
    "naturalFit": [
      {
        "type": "HREP",
        "subtitle": "自由発想のクリエイター",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "自由発想のクリエイターとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HRTJ",
        "subtitle": "技術のパイオニア",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "技術のパイオニアとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CRTP",
        "subtitle": "即興パフォーマー",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "即興パフォーマーは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HSTP",
        "subtitle": "しなやかな技術職人",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "しなやかな技術職人は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CSEJ",
        "subtitle": "信頼を築く推進者",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "信頼を築く推進者とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HREJ: {
    "oneLiner": "新しい技術を見つけ、計画して形にできる人",
    "strengths": [
      {
        "title": "可能性を見つける探究心",
        "description": "まだ広まっていない技術や表現にも興味を持てます。"
      },
      {
        "title": "自分で学ぶ推進力",
        "description": "必要な情報を集め、自分から学びを進められます。"
      },
      {
        "title": "形にする設計力",
        "description": "アイデアを目標と手順に落とし込み、完成まで進められます。"
      }
    ],
    "strengthsSummary": "まだ広まっていない技術や表現にも可能性を感じ、自分で情報を集めながら学びを進められます。思いつきをそのままにせず、目標や手順へ落とし込んで完成まで進められるため、新しい価値を実際の技術へ変えやすい人です。",
    "scenes": [
      {
        "title": "新メニュー開発",
        "description": "情報収集から検証まで自分で組み立て、新しい提案を形にできます。"
      },
      {
        "title": "作品制作",
        "description": "完成イメージから逆算して、技術や準備を整えられます。"
      },
      {
        "title": "技術プロジェクト",
        "description": "目標を決め、周囲を巻き込みながら進行できます。"
      }
    ],
    "scenesSummary": "新メニュー開発や作品制作など、まだ形のないものを具体化する場面で力を発揮します。必要な技術や準備を逆算できるため、長めのプロジェクトでも自分で進行しやすく、新しい価値を結果へつなげられます。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "新しい提案を歓迎する"
      },
      {
        "title": "裁量を持って動ける"
      },
      {
        "title": "技術研究ができる"
      },
      {
        "title": "目標を形にできる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "新しい提案を出せる雰囲気か",
      "自分で学び方を選べるか",
      "作品制作や研究の機会があるか",
      "挑戦を形にする支援があるか"
    ],
    "tiringSituations": [
      {
        "title": "裁量がほとんどない",
        "description": "指示されたことだけを続ける環境では、力を持て余しやすいです。"
      },
      {
        "title": "新しい提案が通らない",
        "description": "変化を避ける文化が強いと、意欲が下がりやすくなります。"
      },
      {
        "title": "目的なく動く",
        "description": "目標や完成形が見えないまま進むと、集中しづらくなります。"
      }
    ],
    "closingMessage": [
      "あなたは未来の可能性を見つけ、それを現実にする力を持っています。思いつきだけで終わらせず、必要な情報や練習を整理すると強みが一段と活きます。新しさと完成度の両方を追いかけることが、あなたらしい成長につながります。"
    ],
    "naturalFit": [
      {
        "type": "HRTJ",
        "subtitle": "技術のパイオニア",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "技術のパイオニアとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HREP",
        "subtitle": "自由発想のクリエイター",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "自由発想のクリエイターとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CREJ",
        "subtitle": "共感のプロデューサー",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "共感のプロデューサーは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HSEJ",
        "subtitle": "自走する技術探究者",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "自走する技術探究者は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CSTP",
        "subtitle": "安心感のムードメーカー",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "安心感のムードメーカーとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  HREP: {
    "oneLiner": "自分らしい発想を、自由に技術と表現へ変える人",
    "strengths": [
      {
        "title": "自由に発想する力",
        "description": "既存の型にとらわれず、新しい表現を考えられます。"
      },
      {
        "title": "感覚を形にする力",
        "description": "頭の中のイメージを、技術や作品として表現できます。"
      },
      {
        "title": "試しながら変える柔軟さ",
        "description": "作りながら方向を変え、より面白い形を見つけられます。"
      }
    ],
    "strengthsSummary": "既存の型にとらわれず、自分の感覚から新しい表現を考えられます。頭の中のイメージを技術や作品として形にすることができ、途中でより良いアイデアが出れば柔軟に方向を変えられる、自由度の高いクリエイティブタイプです。",
    "scenes": [
      {
        "title": "作品撮り",
        "description": "テーマや質感を自由に組み合わせ、自分らしい世界観を作れます。"
      },
      {
        "title": "デザイン提案",
        "description": "定番だけでなく、その人に合う新しい表現を考えられます。"
      },
      {
        "title": "SNS発信",
        "description": "自分の感性を写真やスタイルで自由に発信できます。"
      }
    ],
    "scenesSummary": "作品撮りやデザイン提案、SNS発信など、自分の感性を表現できる場面で力を発揮します。決まった答えに合わせるより、試しながら新しい形を見つけることが得意で、自由度の高いクリエイティブな仕事ほど魅力が出ます。",
    "environment": [
      {
        "title": "表現の自由度が高い"
      },
      {
        "title": "作品づくりができる"
      },
      {
        "title": "新しい発想を歓迎する"
      },
      {
        "title": "個性を活かせる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときも、まずは目の前の課題や仕上がりに意識が向きやすいタイプです。無理に会話を広げるより、きちんとした仕事をすることで信頼を作ろうとします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "言葉で目立つより、技術や行動の質で評価されると自信につながります。自分のこだわりを相手に伝える言葉を少し持てると、良さがさらに理解されやすくなります。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "技術の上達そのものが手応えになりやすく、昨日より精度が上がったことを実感するとモチベーションにつながります。得意分野を一つ持つと、さらに成長を実感しやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『自分が納得できる仕事がしたい』という気持ちを大切にしやすいタイプです。周囲から褒められるだけでなく、自分自身が仕上がりに納得できることが重要です。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "作品撮りや発信の機会があるか",
      "自分の表現を尊重してもらえるか",
      "新しい提案を試せるか",
      "細かなルールで縛られすぎないか"
    ],
    "tiringSituations": [
      {
        "title": "正解を一つに決められる",
        "description": "表現の幅を狭められると、やりがいを感じにくくなります。"
      },
      {
        "title": "毎日が同じ流れ",
        "description": "変化や刺激が少ない状態が続くと、集中力が落ちやすいです。"
      },
      {
        "title": "細かな管理が多い",
        "description": "自由に試す余地が少ないと、発想を活かしにくくなります。"
      }
    ],
    "closingMessage": [
      "あなたの魅力は、まだ形になっていないイメージを自分らしく表現できることです。正解を探しすぎず、まず作ってみることで新しい可能性が見えてきます。自由さの中にも一つだけ得意分野を育てると、個性が強い武器になります。"
    ],
    "naturalFit": [
      {
        "type": "HRTP",
        "subtitle": "ひらめきのチャレンジャー",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "ひらめきのチャレンジャーとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "HREJ",
        "subtitle": "未来を形にする技術開拓者",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "未来を形にする技術開拓者とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "CREP",
        "subtitle": "直感の冒険家",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "直感の冒険家は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『人との関係』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "HSEP",
        "subtitle": "自分流を磨く職人",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "自分流を磨く職人は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "CSTJ",
        "subtitle": "安心と信頼の伴走者",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "安心と信頼の伴走者とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CSTJ: {
    "oneLiner": "人との信頼を、丁寧に長く積み重ねられる人",
    "strengths": [
      {
        "title": "安心感をつくる丁寧さ",
        "description": "相手を急かさず、落ち着いて対応できます。"
      },
      {
        "title": "関係を続ける誠実さ",
        "description": "一度関わった人との約束や会話を大切にできます。"
      },
      {
        "title": "安定して支える力",
        "description": "決めたことを着実に続け、周囲に安心感を与えられます。"
      }
    ],
    "strengthsSummary": "相手を急かさず丁寧に向き合い、一度関わった人との約束や会話を大切にできます。安定した対応を続けることが得意で、派手に目立たなくても、時間をかけて『この人なら安心』と思ってもらえる信頼を築きやすい人です。",
    "scenes": [
      {
        "title": "カウンセリング",
        "description": "相手の話を落ち着いて聞き、希望を丁寧に整理できます。"
      },
      {
        "title": "長期顧客フォロー",
        "description": "前回の会話や髪の変化を覚えながら、関係を育てられます。"
      },
      {
        "title": "チームの支え役",
        "description": "決まった役割を安定してこなし、周囲を安心させられます。"
      }
    ],
    "scenesSummary": "カウンセリングや長期顧客フォローのように、相手とじっくり関係を育てる場面で強みが出ます。チームでも決めた役割を安定して続けられるため、目立つよりも周囲を支えながら信頼を積み上げる仕事で力を発揮します。",
    "environment": [
      {
        "title": "顧客と長く関われる"
      },
      {
        "title": "チームが穏やか"
      },
      {
        "title": "教育方針が明確"
      },
      {
        "title": "働き方が安定している"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "顧客を長く担当できる仕組みか",
      "スタッフ同士の雰囲気が穏やかか",
      "教育の流れが明確か",
      "勤務ルールが安定しているか"
    ],
    "tiringSituations": [
      {
        "title": "人間関係が不安定",
        "description": "周囲の空気が荒れていると、想像以上に気疲れしやすくなります。"
      },
      {
        "title": "急な変更が多い",
        "description": "予定やルールが頻繁に変わると、安心して力を出しにくくなります。"
      },
      {
        "title": "競争が強すぎる",
        "description": "協力より競争ばかりの環境では、自分らしい良さを出しにくくなります。"
      }
    ],
    "closingMessage": [
      "あなたの強みは、派手さよりも安心して任せてもらえる関係を作れることです。小さな約束や丁寧な対応を重ねるほど、その良さは伝わります。自分のペースを守りながら、長く信頼される美容師を目指してください。"
    ],
    "naturalFit": [
      {
        "type": "CSEJ",
        "subtitle": "信頼を築く推進者",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "信頼を築く推進者とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CSTP",
        "subtitle": "安心感のムードメーカー",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "安心感のムードメーカーとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HSTJ",
        "subtitle": "技術を積み上げる職人",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "技術を積み上げる職人は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CRTJ",
        "subtitle": "変化のナビゲーター",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "変化のナビゲーターは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HREP",
        "subtitle": "自由発想のクリエイター",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "自由発想のクリエイターとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CSTP: {
    "oneLiner": "相手に自然に合わせ、居心地のよい空気をつくる人",
    "strengths": [
      {
        "title": "相手に合わせる柔軟さ",
        "description": "相手の雰囲気を見て、自然に接し方を変えられます。"
      },
      {
        "title": "場を和らげる安心感",
        "description": "緊張しやすい場でも、話しやすい空気を作れます。"
      },
      {
        "title": "無理なく支える気配り",
        "description": "必要なときにさりげなく手を差し伸べられます。"
      }
    ],
    "strengthsSummary": "相手の雰囲気に合わせて接し方を自然に変えられ、緊張しやすい場でも話しやすい空気を作れます。必要なときにさりげなく手を差し伸べることも得意で、周囲に『一緒にいると安心する』と思われやすい人です。その積み重ねが、周囲からの信頼にもつながっていきます。",
    "scenes": [
      {
        "title": "初回接客",
        "description": "緊張しているお客様にも、自然な会話で安心感を作れます。"
      },
      {
        "title": "チーム営業",
        "description": "周囲の様子を見て、足りないところを柔軟に補えます。"
      },
      {
        "title": "急な予定変更",
        "description": "場の空気を乱さず、その時できることへ切り替えられます。"
      }
    ],
    "scenesSummary": "初回接客のように相手の緊張をほぐす場面や、チーム営業で周囲を柔軟に支える場面で力を発揮します。急な予定変更にも合わせやすく、その場の空気を壊さず自然に対応できることが大きな強みです。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "人間関係が穏やか"
      },
      {
        "title": "助け合えるチーム"
      },
      {
        "title": "接客を大切にする"
      },
      {
        "title": "柔軟に動ける"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "スタッフ同士が自然に声を掛け合っているか",
      "忙しい時に助け合う雰囲気があるか",
      "接客の距離感を尊重してもらえるか",
      "細かく決められすぎていないか"
    ],
    "tiringSituations": [
      {
        "title": "空気がピリつく",
        "description": "人間関係の緊張が続くと、周囲に気を使いすぎて疲れやすいです。"
      },
      {
        "title": "ルールが細かすぎる",
        "description": "相手に合わせる余地が少ないと、窮屈さを感じやすくなります。"
      },
      {
        "title": "頼まれごとが増える",
        "description": "断らずに受け続けると、自分でも気づかないうちに負担が大きくなります。"
      }
    ],
    "closingMessage": [
      "あなたの良さは、相手を緊張させず自然体で関われることです。型通りに接するより、その人に合わせた距離感を選ぶことで魅力が出ます。周りに合わせすぎず、自分が心地よいペースも大切にしてください。"
    ],
    "naturalFit": [
      {
        "type": "CSEP",
        "subtitle": "自然体のコミュニケーター",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "自然体のコミュニケーターとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CSTJ",
        "subtitle": "安心と信頼の伴走者",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "安心と信頼の伴走者とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HSTP",
        "subtitle": "しなやかな技術職人",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "しなやかな技術職人は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CRTP",
        "subtitle": "即興パフォーマー",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "即興パフォーマーは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HREJ",
        "subtitle": "未来を形にする技術開拓者",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "未来を形にする技術開拓者とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CSEJ: {
    "oneLiner": "信頼をつくりながら、ちゃんと前へ進められる人",
    "strengths": [
      {
        "title": "人に寄り添う観察力",
        "description": "相手の表情や雰囲気の変化に気づき、必要なことを自然に考えられます。"
      },
      {
        "title": "信頼を積み重ねる力",
        "description": "一度関わった人との関係を大切にし、丁寧な対応を続けられます。"
      },
      {
        "title": "着実に前へ進める力",
        "description": "周囲との関係を壊さず、必要なことを整理して自分から動けます。"
      }
    ],
    "strengthsSummary": "相手の表情や雰囲気の小さな変化にも自然と気づき、必要なことを考えられる観察力を持っています。一度関わった相手とは丁寧な対応を重ねて信頼を積み上げ、その関係を大切にしながら自分から着実に前へ進めていける人です。",
    "scenes": [
      {
        "title": "カウンセリング",
        "description": "お客様の言葉だけでなく、表情や反応まで見ながら、本当に求めていることを整理できます。"
      },
      {
        "title": "長期顧客フォロー",
        "description": "髪の変化や過去の会話まで覚えながら、時間をかけて信頼関係を育てられます。"
      },
      {
        "title": "後輩・チームのサポート",
        "description": "周囲の状況を見ながら、必要なことを整理して自然にチームを前へ進められます。"
      }
    ],
    "scenesSummary": "カウンセリングでは言葉だけでなく表情や反応まで見ながら希望を整理でき、長期顧客フォローでは丁寧に信頼を育てられます。チームでも周囲の状況を見ながら必要なことを整理し、自分から動いて前へ進められるタイプです。",
    "environment": [
      {
        "title": "顧客との長期関係"
      },
      {
        "title": "協力型のチーム"
      },
      {
        "title": "ある程度のルールがある"
      },
      {
        "title": "自分で考えて動ける"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "先輩と後輩の距離感は近いか",
      "教育やフォローの進め方が明確か",
      "顧客と長く付き合える環境か",
      "自分で考えて動ける余地があるか"
    ],
    "tiringSituations": [
      {
        "title": "人間関係が不安定",
        "description": "信頼関係を重視するため、空気の悪い職場では想像以上に消耗します。"
      },
      {
        "title": "方針が毎日変わる",
        "description": "ある程度見通しを持って進めたいので、理由なく方針が変わり続ける環境はストレスになりやすいです。"
      },
      {
        "title": "頼まれごとが集中する",
        "description": "断れない状態が続くと、気づかないうちに疲れを溜めやすくなります。"
      }
    ],
    "closingMessage": [
      "あなたの優しさは、ただ人に合わせるためのものではありません。人を理解し、信頼をつくり、その関係を前へ進められることがあなたの強みです。周りを大切にするのと同じくらい、自分の考えも大切にしてください。"
    ],
    "naturalFit": [
      {
        "type": "CSTJ",
        "subtitle": "安心と信頼の伴走者",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "安心と信頼の伴走者とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CSEP",
        "subtitle": "自然体のコミュニケーター",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "自然体のコミュニケーターとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HSEJ",
        "subtitle": "自走する技術探究者",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "自走する技術探究者は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CREJ",
        "subtitle": "共感のプロデューサー",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "共感のプロデューサーは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HRTP",
        "subtitle": "ひらめきのチャレンジャー",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "ひらめきのチャレンジャーとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CSEP: {
    "oneLiner": "自然体のまま、人との距離を心地よくつくれる人",
    "strengths": [
      {
        "title": "自然な距離感をつくる力",
        "description": "相手に無理に踏み込まず、心地よい関係を作れます。"
      },
      {
        "title": "相手に合わせる感覚",
        "description": "人や場の雰囲気を見ながら、接し方を柔軟に変えられます。"
      },
      {
        "title": "自分らしく動く自律性",
        "description": "周りを見ながらも、自分で考えて行動できます。"
      }
    ],
    "strengthsSummary": "相手に無理に踏み込まず、自然で心地よい距離感を作れます。人や場の雰囲気に合わせて接し方を柔軟に変えながらも、周囲に流されるだけではなく、自分で考えて自分らしく動けるバランスの良い人です。その積み重ねが、周囲からの信頼にもつながっていきます。",
    "scenes": [
      {
        "title": "マンツーマン接客",
        "description": "相手のテンポに合わせ、無理のない会話で関係を作れます。"
      },
      {
        "title": "指名顧客との関係づくり",
        "description": "自然体の接客を重ねながら、長く付き合える関係を育てられます。"
      },
      {
        "title": "自由度のある営業",
        "description": "細かな指示がなくても、その場に合わせて自分で判断できます。"
      }
    ],
    "scenesSummary": "マンツーマン接客や指名顧客との関係づくりのように、自然な距離感が大切な場面で力を発揮します。自由度のある営業でも、自分で判断しながら相手に合わせられるため、型にはまりすぎない接客で魅力が出やすいタイプです。",
    "environment": [
      {
        "title": "自然体で接客できる"
      },
      {
        "title": "自分で判断できる"
      },
      {
        "title": "人間関係が穏やか"
      },
      {
        "title": "細かく縛られない"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "関係も仕事も、急に大きく変えるより少しずつ積み重ねる方が安心できます。一度できた信頼ややり方を大切にし、長く続けることで強さを発揮します。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "大きく方向を変えるより、今あるやり方を確認しながら少しずつ改善する方が得意です。安定した流れの中では、無理なく継続して成果を出しやすくなります。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "急激な変化よりも、安心できる土台の上で少しずつ自信を増やすことを好みます。長く続けられること、信頼や技術が積み上がることに意味を感じやすいです。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "接客スタイルに自由度があるか",
      "自分で判断する余地があるか",
      "スタッフ同士の距離感が自然か",
      "細かなルールが多すぎないか"
    ],
    "tiringSituations": [
      {
        "title": "距離が近すぎる",
        "description": "常に濃い人間関係を求められると、気疲れしやすくなります。"
      },
      {
        "title": "細かく管理される",
        "description": "自分で考える余地が少ないと、窮屈さを感じやすいです。"
      },
      {
        "title": "無理に盛り上げる",
        "description": "常に高いテンションを求められると、自分らしさを保ちにくくなります。"
      }
    ],
    "closingMessage": [
      "あなたは、無理に盛り上げなくても人と自然につながれる人です。相手に合わせつつ、自分らしい距離感を保てることが強みです。気を使いすぎるより、自分の感覚も大切にすると関係はもっと心地よくなります。"
    ],
    "naturalFit": [
      {
        "type": "CSTP",
        "subtitle": "安心感のムードメーカー",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "安心感のムードメーカーとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CSEJ",
        "subtitle": "信頼を築く推進者",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "信頼を築く推進者とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HSEP",
        "subtitle": "自分流を磨く職人",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "自分流を磨く職人は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CREP",
        "subtitle": "直感の冒険家",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "直感の冒険家は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『新しい挑戦』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HRTJ",
        "subtitle": "技術のパイオニア",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "技術のパイオニアとは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CRTJ: {
    "oneLiner": "周りを見ながら、変化を前向きに導ける人",
    "strengths": [
      {
        "title": "周囲を巻き込む力",
        "description": "人の反応を見ながら、変化へ一緒に進める空気を作れます。"
      },
      {
        "title": "新しい方向を示す力",
        "description": "現状にとどまらず、次に進む選択肢を考えられます。"
      },
      {
        "title": "変化を整理する計画力",
        "description": "新しいことでも手順を整え、実行しやすい形にできます。"
      }
    ],
    "strengthsSummary": "周囲の反応を見ながら人を巻き込み、現状にとどまらず次の方向を示せます。新しいことを勢いだけで始めるのではなく、手順や役割を整理して実行しやすい形にできるため、チームで変化を進める場面に強い人です。その積み重ねが、周囲からの信頼にもつながっていきます。",
    "scenes": [
      {
        "title": "新しい取り組みの導入",
        "description": "周囲へ目的を伝えながら、変化を進めやすくできます。"
      },
      {
        "title": "チーム改善",
        "description": "みんなの意見を聞きつつ、次にやることを整理できます。"
      },
      {
        "title": "イベント・企画",
        "description": "人を巻き込みながら、準備から当日まで計画的に進められます。"
      }
    ],
    "scenesSummary": "新しい取り組みを導入するときや、チームで改善を進める場面で力を発揮します。イベントや企画でも周囲の意見を聞きつつ段取りを整えられるため、人を巻き込みながら変化を形にする役割に向いています。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "新しい挑戦がある"
      },
      {
        "title": "チームで動ける"
      },
      {
        "title": "提案を歓迎してくれる"
      },
      {
        "title": "役割や目標が明確"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "新しい取り組みに参加できるか",
      "意見を出しやすいチームか",
      "若手の提案を受け入れる文化か",
      "役割や目標が明確に共有されるか"
    ],
    "tiringSituations": [
      {
        "title": "変化を嫌う空気",
        "description": "新しい提案が最初から否定される環境では、意欲が下がりやすいです。"
      },
      {
        "title": "協力が得られない",
        "description": "一人だけで変化を背負う状態が続くと、負担が大きくなります。"
      },
      {
        "title": "段取りがない",
        "description": "方向転換は好きでも、何も整理されないまま進むと疲れやすいです。"
      }
    ],
    "closingMessage": [
      "あなたは、人を置き去りにせず新しい方向へ進められる人です。変化の目的を言葉にし、周囲が動きやすい形へ整理するとさらに力が活きます。自分だけで急がず、みんなが前を向ける進め方を大切にしてください。"
    ],
    "naturalFit": [
      {
        "type": "CREJ",
        "subtitle": "共感のプロデューサー",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "共感のプロデューサーとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CRTP",
        "subtitle": "即興パフォーマー",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "即興パフォーマーとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HRTJ",
        "subtitle": "技術のパイオニア",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "技術のパイオニアは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CSTJ",
        "subtitle": "安心と信頼の伴走者",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "安心と信頼の伴走者は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HSEP",
        "subtitle": "自分流を磨く職人",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "自分流を磨く職人とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CRTP: {
    "oneLiner": "その場の空気をつかみ、瞬時に動きを変えられる人",
    "strengths": [
      {
        "title": "空気を読む瞬発力",
        "description": "相手や場の変化にすぐ気づき、行動を変えられます。"
      },
      {
        "title": "人を巻き込む明るさ",
        "description": "自然な勢いで周囲を動かし、場を活性化できます。"
      },
      {
        "title": "予定外に強い柔軟さ",
        "description": "決めた通りにならなくても、すぐ次の方法を選べます。"
      }
    ],
    "strengthsSummary": "相手や場の変化を瞬時につかみ、必要に応じてすぐ行動を変えられます。自然な勢いで周囲を巻き込み、予定外の出来事にも柔軟に対応できるため、変化が多くスピード感のある現場で強みが出やすい人です。その積み重ねが、周囲からの信頼にもつながっていきます。",
    "scenes": [
      {
        "title": "忙しい営業中",
        "description": "状況を見ながら優先順位をすぐ切り替えられます。"
      },
      {
        "title": "イベント接客",
        "description": "初対面の人とも自然に関わり、その場を盛り上げられます。"
      },
      {
        "title": "急なトラブル対応",
        "description": "予定外の出来事にも、その場で代替案を考えて動けます。"
      }
    ],
    "scenesSummary": "忙しい営業中やイベント接客など、状況が次々変わる場面で力を発揮します。急なトラブルにもその場で代替案を考えられ、初対面の人とも自然に関われるため、ライブ感のある仕事ほど魅力が出ます。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "変化の多い現場"
      },
      {
        "title": "人と関わる機会が多い"
      },
      {
        "title": "その場で判断できる"
      },
      {
        "title": "スピード感がある"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、見本や基本のやり方があり、途中でフィードバックをもらえる環境が合いやすいタイプです。何を直せばよいかが分かると、安心して練習を続けられます。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、教えてもらうことだけに寄りすぎると、自分で判断する経験が増えにくくなります。慣れてきたら、小さなことから自分で決める範囲を増やすと次の成長につながります。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分一人で背負うより、信頼できる人から学びながら確実に成長できる環境に安心を感じます。良い先輩やチームとの出会いが、働きやすさを大きく左右します。"
        ]
      }
    ],
    "visitChecklist": [
      "営業中に自分で判断できるか",
      "いろいろな人と関われるか",
      "変化を楽しむ雰囲気があるか",
      "細かい手順に縛られすぎないか"
    ],
    "tiringSituations": [
      {
        "title": "毎日が単調",
        "description": "同じ流れが長く続くと、集中力を保ちにくくなります。"
      },
      {
        "title": "判断を待たされる",
        "description": "小さなことでも毎回確認が必要だと、動きづらさを感じます。"
      },
      {
        "title": "計画が細かすぎる",
        "description": "予定から外れる余地がないと、自分の柔軟さを活かしにくくなります。"
      }
    ],
    "closingMessage": [
      "あなたの魅力は、今この瞬間に必要なことを感じ取って動けることです。予定通りにいかない場面ほど、その柔軟さが活きます。勢いだけで終わらないよう、時々立ち止まって経験を振り返ると強みがさらに残ります。"
    ],
    "naturalFit": [
      {
        "type": "CREP",
        "subtitle": "直感の冒険家",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "直感の冒険家とは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CRTJ",
        "subtitle": "変化のナビゲーター",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "変化のナビゲーターとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HRTP",
        "subtitle": "ひらめきのチャレンジャー",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "ひらめきのチャレンジャーは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CSTP",
        "subtitle": "安心感のムードメーカー",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "安心感のムードメーカーは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HSEJ",
        "subtitle": "自走する技術探究者",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "自走する技術探究者とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CREJ: {
    "oneLiner": "相手の気持ちを理解し、次の一手を形にできる人",
    "strengths": [
      {
        "title": "相手を理解する共感力",
        "description": "言葉だけでなく、背景や気持ちまで想像できます。"
      },
      {
        "title": "次の一手を考える企画力",
        "description": "相手のニーズから、より良い提案を考えられます。"
      },
      {
        "title": "形にする推進力",
        "description": "アイデアを整理し、実際の行動へ落とし込めます。"
      }
    ],
    "strengthsSummary": "相手の言葉だけでなく背景や気持ちまで想像し、そのニーズから次の提案を考えられます。思いつきで終わらず、アイデアを整理して実際の行動へ落とし込めるため、人の期待を新しい価値へ変えていける人です。その積み重ねが、周囲からの信頼にもつながっていきます。",
    "scenes": [
      {
        "title": "提案型カウンセリング",
        "description": "相手の希望を理解し、一歩先の提案へつなげられます。"
      },
      {
        "title": "顧客向け企画",
        "description": "お客様の反応をもとに、イベントやサービスを考えられます。"
      },
      {
        "title": "チームプロジェクト",
        "description": "人の意見をまとめ、実行までの流れを作れます。"
      }
    ],
    "scenesSummary": "提案型カウンセリングや顧客向け企画など、人の声を新しい価値へ変える場面で力を発揮します。チームでも意見をまとめて実行までの流れを作れるため、接客と企画の両方が必要な仕事で特に強みが出ます。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "提案を歓迎してくれる"
      },
      {
        "title": "顧客の声を活かせる"
      },
      {
        "title": "チームで企画できる"
      },
      {
        "title": "自分で進められる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "物事を進めるときは、ゴールや順番がある程度見えている方が動きやすいタイプです。先にやることを整理すると、落ち着いて力を発揮できます。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、予定通りに進めることを意識しすぎると、急な変更にストレスを感じることがあります。美容の現場では『ここまでは決める、ここからは変えてよい』と余白を持つと動きやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "若手でも提案できる雰囲気か",
      "顧客の声をサービス改善に活かしているか",
      "企画やプロジェクトに参加できるか",
      "自分で考えて動ける裁量があるか"
    ],
    "tiringSituations": [
      {
        "title": "意見を聞いてもらえない",
        "description": "相手を考えた提案が届かない環境では、やりがいを感じにくくなります。"
      },
      {
        "title": "周囲の期待が集中する",
        "description": "頼られ続けると、自分の負担を後回しにしやすくなります。"
      },
      {
        "title": "実行に移せない",
        "description": "アイデアを出しても形にできない状態が続くと、もどかしさを感じます。"
      }
    ],
    "closingMessage": [
      "あなたは、人の気持ちを理解するだけでなく、その先の行動まで考えられる人です。相手の声を拾い、具体的な提案へ変えるほど強みが際立ちます。周囲の期待を背負いすぎず、自分が大切にしたい方向も忘れないでください。"
    ],
    "naturalFit": [
      {
        "type": "CRTJ",
        "subtitle": "変化のナビゲーター",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "変化のナビゲーターとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CREP",
        "subtitle": "直感の冒険家",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "直感の冒険家とは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HREJ",
        "subtitle": "未来を形にする技術開拓者",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "未来を形にする技術開拓者は、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CSEJ",
        "subtitle": "信頼を築く推進者",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "信頼を築く推進者は、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HSTP",
        "subtitle": "しなやかな技術職人",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "しなやかな技術職人とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
  CREP: {
    "oneLiner": "人やチャンスを感じたら、まず動いて広げられる人",
    "strengths": [
      {
        "title": "チャンスをつかむ直感",
        "description": "人や出来事から、新しい可能性を素早く感じ取れます。"
      },
      {
        "title": "人を巻き込む行動力",
        "description": "興味を持ったことへ自分から動き、周囲も巻き込めます。"
      },
      {
        "title": "変化を楽しむ柔軟さ",
        "description": "予定外の流れも前向きに受け入れ、次の選択肢を見つけられます。"
      }
    ],
    "strengthsSummary": "人や出来事から新しい可能性を素早く感じ取り、興味を持ったことへ自分から動けます。周囲を巻き込みながら行動を広げ、予定外の変化も次のチャンスとして受け入れられるため、出会いと変化が多い環境ほど力が出やすい人です。",
    "scenes": [
      {
        "title": "新規顧客との出会い",
        "description": "初対面でも自然に関係を作り、次の機会へつなげられます。"
      },
      {
        "title": "SNS・イベント",
        "description": "人の反応を見ながら、新しい発信や企画へすぐ動けます。"
      },
      {
        "title": "新しい挑戦",
        "description": "面白そうだと感じたことを、まず行動に移して経験できます。"
      }
    ],
    "scenesSummary": "新規顧客との出会いやSNS、イベントなど、人と新しい機会が交わる場面で力を発揮します。面白そうだと感じたことをすぐ行動に移せるため、変化のある環境で経験を増やしながら、自分の可能性を広げていけます。経験を重ねるほど、その持ち味がさらに磨かれていきます。",
    "environment": [
      {
        "title": "人との出会いが多い"
      },
      {
        "title": "新しい挑戦ができる"
      },
      {
        "title": "自由に提案できる"
      },
      {
        "title": "変化を楽しめる"
      }
    ],
    "personalitySections": [
      {
        "title": "人との関わり方",
        "paragraphs": [
          "人と関わるときは、相手の表情や反応を自然に見ながら距離感を調整しやすいタイプです。何をしたかだけでなく、相手がどう感じたかも大切にします。",
          "同じ関係ややり方に固定されるより、新しい人や考え方に触れることで刺激を受けます。変化がある場面でも、その時の状況を見ながら前向きに関われます。",
          "相手に合わせる力がある分、気づかないうちに自分の希望を後回しにすることもあります。相手を大切にしながら、自分の考えも言葉にできると関係がより安定します。"
        ]
      },
      {
        "title": "物事の進め方",
        "paragraphs": [
          "最初から細かく決め切るより、まず動きながら次を考える方が自然なタイプです。状況に合わせて順番や方法を変えられることが強みです。",
          "変化がある方が集中しやすく、新しい方法や役割にも比較的入りやすいです。今より良い方法が見えると、現状にこだわらず切り替えられます。",
          "一方で、目の前のことに合わせ続けると、長期的な目標が後回しになりやすい面もあります。大事な締切や目標だけは先に決めておくと、柔軟さが成果につながりやすくなります。"
        ]
      },
      {
        "title": "成長するときの特徴",
        "paragraphs": [
          "成長するときは、自分で課題を見つけて調べたり試したりする余地があるほど意欲が出やすいタイプです。細かく指示されるより、自分で工夫できる方が吸収しやすくなります。",
          "人からの反応や信頼が成長の手応えになりやすく、『喜んでもらえた』『任せてもらえた』という経験が自信につながります。接客と技術をセットで振り返ると伸びやすくなります。",
          "ただし、一人で考え続けると自分では気づけない癖を見落とすことがあります。必要なタイミングでは先輩の視点を借りることで、自律性を保ったまま成長を加速できます。"
        ]
      },
      {
        "title": "心の奥で大切にしていること",
        "paragraphs": [
          "心の奥では、『人と良い関係を作りながら働きたい』という気持ちを大切にしやすいタイプです。成果だけでなく、誰とどんな空気で働くかも満足度に大きく影響します。",
          "同じ状態を守ることよりも、新しい経験によって可能性が広がることに魅力を感じます。挑戦の中で『できることが増えた』と感じると、大きなやりがいになります。",
          "自分の考えや選択を尊重してもらえることが重要です。任せてもらえるほど責任感が生まれ、自分らしい工夫や成長につながりやすくなります。"
        ]
      }
    ],
    "visitChecklist": [
      "新しい人や仕事に出会える機会があるか",
      "若手でも挑戦できるか",
      "自分から企画や発信を提案できるか",
      "変化を前向きに楽しむ文化か"
    ],
    "tiringSituations": [
      {
        "title": "変化がほとんどない",
        "description": "刺激が少ない状態が続くと、意欲を保ちにくくなります。"
      },
      {
        "title": "行動を細かく制限される",
        "description": "自分から動く余地が少ないと、窮屈さを感じやすいです。"
      },
      {
        "title": "一つだけに固定される",
        "description": "興味を広げる機会がないと、可能性を狭められたように感じやすいです。"
      }
    ],
    "closingMessage": [
      "あなたは、人との出会いや新しい機会から可能性を見つけられる人です。直感を行動に変える速さは大きな武器です。興味が広がりすぎたときは、今一番大切なものを一つ選ぶと、行動が成果へつながりやすくなります。"
    ],
    "naturalFit": [
      {
        "type": "CRTP",
        "subtitle": "即興パフォーマー",
        "note": "成長の仕方が補い合う",
        "paragraphs": [
          "即興パフォーマーとは、仕事の土台となる価値観が近いため、考え方を理解しやすい組み合わせです。学び方には違いがありますが、同じ方向を見ながら『教わる／自分で考える』を補い合えます。役割を決めすぎず、お互いの得意な進め方を尊重すると自然に噛み合いやすくなります。"
        ]
      },
      {
        "type": "CREJ",
        "subtitle": "共感のプロデューサー",
        "note": "進め方の違いを活かせる",
        "paragraphs": [
          "共感のプロデューサーとは、大切にすることや成長の仕方が近く、安心して一緒に動きやすい組み合わせです。計画性と柔軟性の違いがあるため、一方が道筋を作り、もう一方が現場に合わせる形で補い合えます。違いを優劣ではなく役割として見ると強いチームになります。"
        ]
      }
    ],
    "broadening": [
      {
        "type": "HREP",
        "subtitle": "自由発想のクリエイター",
        "note": "価値の置き方が違う",
        "paragraphs": [
          "自由発想のクリエイターは、同じような成長・進行スタイルを持ちながら、仕事で重視するものが異なるタイプです。自分では見落としやすい『技術や完成度』の視点を持ち込んでくれるため、考え方を広げてくれます。相手の優先順位を理解すると、刺激のある良い組み合わせになります。"
        ]
      },
      {
        "type": "CSEP",
        "subtitle": "自然体のコミュニケーター",
        "note": "変化への向き合い方が違う",
        "paragraphs": [
          "自然体のコミュニケーターは、基本的な価値観は近い一方で、安定と変化への向き合い方が異なります。自分では選びにくい『着実な積み上げ』を見せてくれるため、行動の幅を広げてくれます。ペースの違いを先に理解しておくと、互いの良さを活かしやすくなります。"
        ]
      }
    ],
    "needsAwareness": [
      {
        "type": "HSTJ",
        "subtitle": "技術を積み上げる職人",
        "note": "4つの軸が大きく異なる",
        "paragraphs": [
          "技術を積み上げる職人とは、重視すること・変化への向き合い方・成長の仕方・進め方が大きく異なります。最初は『なぜそうするのか』が分かりにくいことがありますが、相手が間違っているというより判断基準が違うだけです。結論だけでなく理由や期待する役割を言葉にすると、ズレを減らしやすくなります。"
        ]
      }
    ]
  },
};
